import {
  useLeadsContainerStore,
  useLeadsSheetStore,
  useLeadsStore,
} from "../../../../../../stores/LeadsStore";
import { Drawer, LoadingOverlay, Stack } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { findProspect } from "../../../../../../api/PRMAPI";
import { groupByMonthAndProgId, parseProspectDatafields } from "../../../utils";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../../../../stores/LanguageStore";
import LeadsSheetMobileMain from "./LeadsSheetMobileMain";
import TabNotesMobile from "./TabNotesMobile";
import LeadsSheetMobileHeading from "./LeadsSheetMobileHeading";
import TabRemindersMobile from "./TabRemindersMobile";
import TabAnswersMobile from "./TabAnswersMobile";
import LeadSheetArrows from "./LeadSheetArrows";

const LeadSheetMobile = () => {
  const lang = useLanguage((s) => s.language);
  const opened = useLeadsSheetStore((s) => s.opened);
  const setOpened = useLeadsSheetStore((s) => s.setOpened);

  const current = useLeadsSheetStore((s) => s.current);
  const currentContainer = useLeadsSheetStore((s) => s.currentContainer);
  const updateContainerItem = useLeadsContainerStore(
    (s) => s.updateContainerItem
  );
  const setCurrent = useLeadsSheetStore((s) => s.setCurrent);
  const setLoading = useLeadsSheetStore((s) => s.setLoading);
  const loading = useLeadsSheetStore((s) => s.loading);
  const [active, setActive] = useState("main");

  //#region About
  const about = useMemo(() => {
    const aboutList = [
      { key: "%last_name%", value: "lastName" },
      { key: "%first_name%", value: "firstName" },
      { key: "%job_title%", value: "jobTitle" },
      { key: "%company%", value: "company" },
      { key: "%phone%", value: "phone" },
      { key: "%email%", value: "email" },
      { key: "%contact_image_url%", value: "contact_image_url" },
    ];
    let result = {};

    aboutList.forEach((item) => {
      result[item.value] =
        current?.properties?.find((x) => x.identifier_placeholder === item.key)
          ?.value || " - ";
    });

    return result;
  }, [current?.properties]);

  const conversations = useMemo(() => {
    if (!current?.programmations && !current?.history) {
      return [];
    }

    const programmations = current?.programmations || [];
    const history = current?.history || [];

    const result = [];
    const unlinkedItems = [];

    for (const prog of programmations) {
      const matchedItems = history.filter((x) => {
        return (
          x?.workflow_programmation_id === prog.programmation_id &&
          (x.history_type === "sent" ||
            x.history_type === "reply" ||
            x?.event === "email_sent")
        );
      });

      result.push({
        ...prog,
        items: matchedItems,
      });
    }

    // Find unlinked items (no matching programmation_id)
    for (const x of history) {
      if (
        (x.history_type === "sent" || x.history_type === "reply") &&
        !x?.workflow_programmation_id
      ) {
        unlinkedItems.push(x);
      }
    }

    // Add a synthetic "Unlinked" group if needed
    if (unlinkedItems.length > 0) {
      result.push({
        programmation_id: null,
        name: "Unlinked Activities",
        items: unlinkedItems,
      });
    }

    return groupByMonthAndProgId(result);
  }, [current]);

  //#endregion

  useEffect(() => {
    if (loading && current?.id) {
      findProspect(current?.id, true)
        .then((response) => {
          setCurrent({
            ...parseProspectDatafields(response.data.contact_profile),
            unsubscribed: current?.unsubscribed,
            new_reply: false,
          });

          if (current?.new_reply === true) {
            updateContainerItem(currentContainer?.id, current.id, {
              new_reply: false,
            });
            useLeadsStore
              .getState()
              .setNewsCount(useLeadsStore.getState().newsCount - 1);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [current?.id, loading, setCurrent, setLoading]);

  useEffect(() => {
    if (opened) {
      setLoading(true);
    }
  }, [opened]);

  const navigate = useNavigate();
  const handleSheetClose = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("contact");
    searchParams.delete("container");
    searchParams.delete("index");
    searchParams.delete("reply");

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: false }
    );
    setOpened(false);
    setActive("main");
  };

  return (
    <Drawer
      offset={8}
      radius="md"
      opened={opened}
      onClose={handleSheetClose}
      overlayProps={{ backgroundOpacity: 0.5, blur: 1 }}
      size={"100%"}
      position="right"
      withCloseButton={false}
      styles={{
        body: {
          height: "100%",
          padding: 0,
        },
      }}
    >
      <Stack
        style={{
          borderRadius: 8,
          background: "var(--mantine-color-primary-2)",
        }}
        p={"md"}
        mih={"100%"}
      >
        <LeadsSheetMobileHeading
          about={about}
          active={active}
          setActive={setActive}
          handleSheetClose={handleSheetClose}
        />
        <LoadingOverlay visible={loading} />
        {active === "main" && (
          <LeadsSheetMobileMain
            current={current}
            about={about}
            setActive={setActive}
            conversations={conversations}
          />
        )}
        {active === "notes" && <TabNotesMobile />}
        {active === "reminders" && <TabRemindersMobile />}
        {active === "conversations" && (
          <TabAnswersMobile items={conversations} />
        )}
      </Stack>
    </Drawer>
  );
};

export default LeadSheetMobile;
